// Select and Map (`CompilePipeline.run` and its helpers, SuiteCompile/Pipeline.swift): the arrangement's tree walked
// in order, each item given a role by the preset's rules (section types, structure, collections), book matter and a
// journal's opening and sign-off around it, or a single Submission document instead. The result is a
// `CompiledManuscript` every writer takes. `sample()` (Publish.swift) is here too.
import {
  arrangementTree, BookMatter, emptyPublication, escape, fountainParagraphs, hasOwnPostMatter, isStageFormat, songsIn, isNumberedChapter, JournalPosts, journalPosts, nextPostNumber, postContaining, parse, postNumberProblems, publicationPlaceholders, StandardField,
  trimAll, withoutMarks, wordCountOfBlocks, type ArrangementTreeNode, type Block,
} from '@writing-suite/core';
import { castOf } from './screenplay/stage.ts';
import { authorNames, bylineName, publicationEdition, roundedWords, shortTitle as makeShortTitle } from './manuscript.ts';
import { applyingLinks } from './links.ts';
import { CompileError } from './presets.ts';
import { collectionMembers, displayValue, type CompileSource, type SourceFragment } from './source.ts';
import { isNamed, isUnsupported } from '@writing-suite/core/plan';
import type { AuthorInfo, CompiledManuscript, CompiledSection, CompilePreset, Condition, SectionRole } from './types.ts';

/** `CompilePipeline.blocks`: prose as compile reads it. The writer's highlights and text colours stay on the blocks
 *  until `applyingMarks` decides, from the preset, whether the output keeps them. */
export function compileBlocks(text: string): Block[] {
  return parse(text);
}

/** `Block.withoutMarks`. */
export function blockWithoutMarks(block: Block): Block {
  return { ...block, inlines: block.inlines.map((i) => (i.type === 'text' ? { ...i, style: withoutMarks(i.style) } : i)) };
}

/** `Block.hasMarks`: any text in the block carries a highlight or a text colour. */
export function blockHasMarks(block: Block): boolean {
  return block.inlines.some((i) => i.type === 'text' && (i.style.highlight !== null || i.style.color !== null));
}

/** `CompilePipeline.keepsMarks`: the output carries the writer's highlights and text colours only in a Word file whose
 *  preset asks (`render.keepHighlights`) — an editor's copy, where the marks are notes. */
export function keepsMarks(preset: CompilePreset): boolean {
  return preset.format === 'docx' && preset.render.keepHighlights === true;
}

/** `CompilePipeline.applyingMarks`: the manuscript with its marks kept or dropped as `keepsMarks` says, remembering
 *  whether there were any (`hasMarks`), so Publish can say what the file will leave out. */
export function applyingMarks(m: CompiledManuscript): CompiledManuscript {
  const hasMarks = m.sections.some((s) => s.blocks.some(blockHasMarks));
  if (!hasMarks) return m;
  if (keepsMarks(m.preset)) return { ...m, hasMarks };
  return { ...m, hasMarks, sections: m.sections.map((s) => ({ ...s, blocks: s.blocks.map(blockWithoutMarks) })) };
}

const nodeFragment = (source: CompileSource, n: ArrangementTreeNode) =>
  n.node.fragmentID !== undefined ? source.fragments.get(n.node.fragmentID) : undefined;

function section(o: Partial<CompiledSection> & Pick<CompiledSection, 'role' | 'title' | 'number' | 'blocks'>): CompiledSection {
  return { fragmentID: null, followsScene: false, matter: null, source: null, kind: null, numbered: true, ...o };
}

export interface RunOptions {
  /** Defaults to now; the year for `{year}` and the date a post without its own takes. */
  date?: Date;
  /** The post (node or fragment id) a one-post preset compiles. */
  only?: string | null;
}

/** `CompilePipeline.run`. */
export function run(preset: CompilePreset, source: CompileSource, author: AuthorInfo | null, o: RunOptions = {}): CompiledManuscript {
  const date = o.date ?? new Date();
  const only = o.only ?? null;
  let arrangement;
  if (preset.select.arrangement.toLowerCase() === 'manuscript') {
    arrangement = source.arrangements.find((a) => a.role === 'manuscript');
    if (!arrangement) throw new CompileError({ type: 'noArrangement', name: 'manuscript' });
  } else {
    const wanted = preset.select.arrangement;
    arrangement = source.arrangements.find((a) => a.name.toLowerCase() === wanted.toLowerCase() || a.id === wanted);
    if (!arrangement) throw new CompileError({ type: 'noArrangement', name: wanted });
  }

  // A journal compiles one post (the one holding a section, if a section is named), or its posts oldest first.
  let roots = arrangementTree(arrangement);
  const posts = journalPosts(roots);
  const postNodes = new Set(posts.map((p) => p.nodeId)), sectionNodes = new Set(posts.flatMap((p) => p.sections.map((s) => s.nodeId)));
  const place = (node: ArrangementTreeNode): PostPlace => (postNodes.has(node.node.id) ? 'post' : sectionNodes.has(node.node.id) ? 'inPost' : 'neither');
  if (preset.select.onePost === true) {
    const find = (id: string, nodes: ArrangementTreeNode[]): ArrangementTreeNode | null => {
      for (const n of nodes) {
        if (n.node.id === id || n.node.fragmentID === id) return n;
        const hit = find(id, n.children);
        if (hit) return hit;
      }
      return null;
    };
    const post = only !== null ? find(postContaining(only, posts)?.nodeId ?? only, roots) : null;
    if (!post) throw new CompileError({ type: 'noPost' });
    roots = [post];
  }
  if (preset.select.reversed === true) {
    const reversed = (nodes: ArrangementTreeNode[]): ArrangementTreeNode[] => [...nodes].reverse().map((n) => ({ ...n, children: reversed(n.children) }));
    roots = reversed(roots);
  }

  // Every collection a condition names must exist: a misspelt name would quietly compile nothing.
  const conditions = [...preset.select.exclude, ...(preset.select.include ?? []), ...preset.map.rules.map((r) => r.when)];
  for (const name of new Set(conditions.flatMap((c) => (c.collection !== undefined ? [c.collection] : [])))) {
    if (collectionMembers(source, name) === null) throw new CompileError({ type: 'invalidPreset', id: preset.id, why: `there's no collection called “${name}”` });
    // One made by a newer version (a rule or scope this build can't read) would quietly compile nothing too.
    if (source.savedSearches.some((s) => isNamed(s, name) && isUnsupported(s))) {
      throw new CompileError({ type: 'invalidPreset', id: preset.id, why: `the collection “${name}” was made in a newer version of Five Rooms` });
    }
  }

  // `include`: the nodes that match one condition, and the folders above them.
  const matching = new Set<string>(), kept = new Set<string>();
  const include = preset.select.include ?? [];
  const limit = source.limit ?? null;
  const filtering = include.length > 0 || limit !== null;
  if (filtering) {
    const mark = (nodes: ArrangementTreeNode[], depth: number): boolean => {
      let any = false;
      for (const node of nodes) {
        const fragment = nodeFragment(source, node);
        const own = fragment
          ? (include.length === 0 || include.some((c) => matches(c, fragment, node, depth, source, place(node)))) && (limit?.has(fragment.id) ?? true)
          : false;
        if (own) matching.add(node.node.id);
        if (mark(node.children, depth + 1) || own) { kept.add(node.node.id); any = true; }
      }
      return any;
    };
    mark(roots, 0);
  }

  let sections: CompiledSection[] = [];
  const counters = new Map<SectionRole, number>();
  let lastWasSceneInChapter = false;

  const walk = (nodes: ArrangementTreeNode[], depth: number) => {
    for (const node of nodes) {
      const fragment = nodeFragment(source, node);
      // An `exclude` rule or a `skip` role leaves out the whole subtree; Include in Compile is per item.
      if (fragment && preset.select.exclude.some((c) => matches(c, fragment, node, depth, source, place(node)))) continue;
      if (filtering && !kept.has(node.node.id)) continue;
      const rule = preset.map.rules.find((r) => (fragment ? matches(r.when, fragment, node, depth, source, place(node)) : structuralMatch(r.when, node, depth, place(node))));
      const role: SectionRole = rule?.role ?? 'scene';
      if (role === 'skip') continue;
      const included = !(fragment && preset.select.respectIncludeInCompile && !fragment.includeInCompile);
      if (included && rule?.join === true && sections.length > 0) {
        // Joined: its text goes on the end of the section before it, which stays the one section.
        if (fragment) {
          const headingOnly = filtering && !matching.has(node.node.id);
          sections[sections.length - 1] = joining(headingOnly ? [] : compileBlocks(fragment.text), fragment.id, sections[sections.length - 1]);
        }
      } else if (included) {
        // An unnumbered chapter or part (a prologue) doesn't advance the count.
        const numbered = !(role === 'chapter' || role === 'part') || isNumberedChapter(fragment?.metadata);
        if (numbered) counters.set(role, (counters.get(role) ?? 0) + 1);
        if (role === 'chapter' || role === 'part') lastWasSceneInChapter = false;
        // A folder kept only for what's inside it is a heading, without its own text.
        const headingOnly = filtering && !matching.has(node.node.id);
        const blocks = headingOnly ? [] : fragment ? compileBlocks(fragment.text) : [];
        const followsScene = role === 'scene' && lastWasSceneInChapter && blocks.length > 0;
        sections.push(section({
          role, title: fragment?.title ?? node.node.title ?? '', number: counters.get(role) ?? 0, fragmentID: fragment?.id ?? null,
          blocks, followsScene, source: headingOnly ? null : fragment?.text ?? null, kind: fragment?.kind ?? null, numbered,
        }));
        if (role === 'scene' && blocks.length > 0) lastWasSceneInChapter = true;
      }
      walk(node.children, depth + 1);
    }
  };
  walk(roots, 0);

  // Front and back matter from their folders; the manuscript's word count leaves them out.
  const body = sections;
  const shortTitle = source.shortTitle ?? makeShortTitle(source.title);
  const year = date.getFullYear();
  const publication = preset.blind ? {} : publicationPlaceholders(source.publication, {
    author: author ? bylineName(author) : null, year, edition: publicationEdition(preset), typeface: preset.render.print ? preset.render.font.family : null,
    ...(author && authorNames(author).length > 1 ? { authors: authorNames(author).length } : {}),
  });
  const fill = (text: string) => BookMatter.fillBook(text, {
    title: source.title, author: preset.blind || !author ? null : bylineName(author), shortTitle, year, extra: publication,
  });
  if (preset.select.frontMatter) sections = [...matterSections('front-matter', 'front-matter', source, preset, fill), ...sections];
  if (preset.select.backMatter) sections = [...sections, ...matterSections('back-matter', 'back-matter', source, preset, fill)];

  const words = body.reduce((total, s) => total + wordCountOfBlocks(s.blocks), 0);
  // A journal's opening and sign-off go around every post, and never count towards its words; a post that carries its
  // own copy (New Post's) has that copy's placeholders filled in every journal format.
  let provisional: Record<string, number> = {};
  if (preset.select.postMatter === true || preset.journal === true) ({ sections, provisional } = wrapPosts(sections, source, preset, author, date));

  const base = {
    title: source.title, shortTitle, language: source.language, projectID: source.projectID,
    audiobook: { narrator: '', engine: 'system' as const, pronunciations: {} }, publication: emptyPublication(),
    mergeTags: [], provisionalNumbers: {}, postNumberProblems: { shared: [], missing: [] }, readerMark: null,
  };

  // A single Submission document (synopsis, query letter) instead of the book.
  if (preset.select.document !== undefined) {
    const kind = BookMatter.from(preset.select.document);
    if (!kind) throw new CompileError({ type: 'invalidPreset', id: preset.id, why: `unknown document “${preset.select.document}”` });
    const fillDocument = (text: string) => BookMatter.fillBook(text, {
      title: source.title, author: author ? bylineName(author) : null, shortTitle, year, words: roundedWords(words),
    });
    let document: CompiledSection;
    try {
      document = documentSection(kind, source, fillDocument);
    } catch (e) {
      // A play's Cast of Characters left empty: made from who speaks, as the play's own cast page is.
      // A musical's numbers the same, from its songs.
      const k = e instanceof CompileError ? e.kind : null;
      if ((kind !== 'cast-list' && kind !== 'musical-numbers') || !k || (k.type !== 'emptyDocument' && k.type !== 'noDocument')) throw e;
      const title = k.type === 'emptyDocument' ? k.title : BookMatter.title(kind);
      document = kind === 'cast-list' ? generatedCastList(sections, source, title) : generatedMusicalNumbers(sections, source, title);
    }
    return applyingMarks({
      ...base, sections: applyingLinks([document], preset), wordCount: wordCountOfBlocks(document.blocks), author, preset,
      manuscriptWordCount: words,
    });
  }
  const result: CompiledManuscript = {
    ...base, sections: applyingLinks(sections, preset), wordCount: words, author: preset.blind ? null : author, preset, manuscriptWordCount: null,
    mergeTags: [...source.mergeTags], provisionalNumbers: provisional, characters: [...(source.characters ?? [])],
    musical: source.musical ?? false,
  };
  if (preset.journal === true || preset.select.postMatter === true) {
    const own = sections.flatMap((s) => {
      const f = s.fragmentID !== null ? source.fragments.get(s.fragmentID) : undefined;
      return f && hasOwnPostMatter(f.metadata) ? [f.id] : [];
    });
    if (own.length) result.ownPostMatter = [...new Set(own)];
  }
  if (preset.journal === true) {
    const manuscript = source.arrangements.find((a) => a.role === 'manuscript');
    if (manuscript) {
      const numbers = journalPosts(manuscript).flatMap(({ fragmentId: id }) => {
        const f = source.fragments.get(id);
        const n = f ? JournalPosts.number(f.metadata) : null;
        return n !== null ? [n] : [];
      });
      result.postNumberProblems = postNumberProblems(numbers);
    }
  }
  result.audiobook = structuredClone(source.audiobook);
  if (preset.blind) result.audiobook.narrator = '';
  else result.publication = structuredClone(source.publication);
  return applyingMarks(result);
}

/** `wrapPosts`: the journal's Opening before and Sign-off after each post when the preset asks (`select.postMatter`),
 *  filled for that post; a post carrying its own copy (`hasOwnPostMatter`) gets no second one, its own text filled instead,
 *  whatever the journal preset. */
export function wrapPosts(sections: CompiledSection[], source: CompileSource, preset: CompilePreset, author: AuthorInfo | null,
  date: Date): { sections: CompiledSection[]; provisional: Record<string, number> } {
  const text = (kind: BookMatter): string | null => {
    if (preset.select.postMatter !== true) return null;
    const f = documentFragment(kind, source);
    if (!f || (preset.select.respectIncludeInCompile && !f.includeInCompile)) return null;
    return trimAll(f.text) === '' ? null : f.text;
  };
  const opening = text('post-opening'), signOff = text('post-sign-off');
  const ownMatter = (s: CompiledSection) =>
    s.source !== null && s.fragmentID !== null && hasOwnPostMatter(source.fragments.get(s.fragmentID)?.metadata);
  if (opening === null && signOff === null && !sections.some(ownMatter)) return { sections, provisional: {} };
  let next = nextPostNumber([...source.fragments.values()].map((f) => f.metadata), source.firstPostNumber);
  const provisional: Record<string, number> = {};
  let dates: Intl.DateTimeFormat;
  try { dates = new Intl.DateTimeFormat(source.language || 'en', { dateStyle: 'long' }); } catch { dates = new Intl.DateTimeFormat('en', { dateStyle: 'long' }); }
  const wrapped = sections.map((s) => {
    const id = s.fragmentID;
    const fragment = id !== null ? source.fragments.get(id) : undefined;
    if (s.matter !== null || !(s.role === 'chapter' || s.role === 'scene') || s.blocks.length === 0 || id === null || !fragment) return s;
    const own = ownMatter(s);
    if (!own && opening === null && signOff === null) return s;
    let number = JournalPosts.number(fragment.metadata);
    if (number === null) {
      number = next;
      provisional[id] = next;
      next += 1;
    }
    const published = fragment.metadata[StandardField.publishDate]?.type === 'date' ? (fragment.metadata[StandardField.publishDate].value as Date) : null;
    const tags = fragment.metadata[StandardField.tags]?.type === 'text' ? (fragment.metadata[StandardField.tags].value as string) : '';
    const values: Record<string, string> = {
      number: String(number), title: fragment.title, journal: source.title, author: preset.blind || !author ? '' : bylineName(author),
      date: published ? dates.format(published) : '', year: String((published ?? date).getFullYear()), tags,
    };
    if (own) {
      // Its own text comes first in the section (any sections of the post are joined after it).
      const filled = BookMatter.fill(fragment.text, values);
      return { ...s, blocks: [...compileBlocks(filled), ...s.blocks.slice(compileBlocks(fragment.text).length)], source: filled };
    }
    const before = opening !== null ? compileBlocks(BookMatter.fill(opening, values)) : [];
    const after = signOff !== null ? compileBlocks(BookMatter.fill(signOff, values)) : [];
    // Notes stay at the end, after the sign-off.
    const notes = s.blocks.filter((b) => b.kind.type === 'footnote');
    const rest = s.blocks.filter((b) => b.kind.type !== 'footnote');
    return { ...s, blocks: [...before, ...rest, ...after, ...notes] };
  });
  return { sections: wrapped, provisional };
}

/** `documentFragment`: the project's document of this kind (a `book-matter` marker), outside the trash. */
export function documentFragment(kind: BookMatter, source: CompileSource): SourceFragment | null {
  let found: SourceFragment | null = null;
  const find = (nodes: ArrangementTreeNode[]) => {
    for (const n of nodes) {
      if (found) return;
      const f = nodeFragment(source, n);
      const marker = f?.metadata[BookMatter.field];
      if (f && marker?.type === 'text' && marker.value === kind) found = f;
      find(n.children);
    }
  };
  for (const a of source.arrangements) if (a.role !== 'trash') find(arrangementTree(a));
  return found;
}

/** `CompilePipeline.generatedCastList`: a character breakdown from the play's cues (most speeches first), each with the
 *  cast's role for them. */
export function generatedCastList(sections: readonly CompiledSection[], source: CompileSource, title: string): CompiledSection {
  const paragraphs = sections.flatMap((s) => (s.source !== null ? fountainParagraphs(s.source) : []));
  const cast = castOf([{ act: null, scene: null, headings: [], paragraphs, end: null }], source.characters ?? []);
  if (!cast.length) throw new CompileError({ type: 'emptyDocument', title });
  const text = cast.map((c) => `**${escape(c.name)}**` + (c.description === '' ? '' : `: ${escape(c.description)}`)).join('\n\n');
  return section({ role: 'scene', title, number: 1, fragmentID: documentFragment('cast-list', source)?.id ?? null, blocks: compileBlocks(text), matter: 'cast-list' });
}

/** `CompilePipeline.generatedMusicalNumbers`: a musical's numbers from its songs: each act's name in bold, then
 *  "1. The River's High — MARA, TOM". */
export function generatedMusicalNumbers(sections: readonly CompiledSection[], source: CompileSource, title: string): CompiledSection {
  const lines: string[] = [];
  let number = 0;
  for (const s of sections) {
    if (s.matter !== null) continue;
    // An act: a folder (a part or a chapter, as the preset maps it) with no text of its own.
    const text = s.source ?? '';
    if ((s.role === 'part' || s.role === 'chapter') && /^\p{White_Space}*$/u.test(text)) {
      lines.push(`**${escape(s.title)}**`);
      continue;
    }
    for (const song of songsIn(fountainParagraphs(text))) {
      number += 1;
      const singers = song.singers.length ? ' — ' + song.singers.join(', ') : '';
      lines.push(escape(`${number}. ${song.title}${singers}`));
    }
  }
  if (number === 0) throw new CompileError({ type: 'emptyDocument', title });
  // An act with no songs isn't listed.
  const kept = lines.filter((line, i) => !(line.startsWith('**') && (i + 1 >= lines.length || lines[i + 1].startsWith('**'))));
  return section({ role: 'scene', title, number: 1, fragmentID: documentFragment('musical-numbers', source)?.id ?? null,
    blocks: compileBlocks(kept.join('\n\n')), matter: 'musical-numbers' });
}

/** `documentSection`: the project's document of this kind, filled in. */
export function documentSection(kind: BookMatter, source: CompileSource, fill: (text: string) => string): CompiledSection {
  const fragment = documentFragment(kind, source);
  if (!fragment) throw new CompileError({ type: 'noDocument', title: BookMatter.title(kind) });
  const text = fill(fragment.text);
  if (trimAll(text) === '') throw new CompileError({ type: 'emptyDocument', title: fragment.title });
  return section({ role: 'scene', title: fragment.title, number: 1, fragmentID: fragment.id, blocks: compileBlocks(text), matter: kind });
}

/** `matterSections`: the documents directly inside a book-matter folder, in order, filled; generated pages kept. */
export function matterSections(folder: BookMatter, role: SectionRole, source: CompileSource, preset: CompilePreset,
  fill: (text: string) => string): CompiledSection[] {
  const marker = (f: SourceFragment): BookMatter | null => {
    const m = f.metadata[BookMatter.field];
    return m?.type === 'text' ? BookMatter.from(m.value) : null;
  };
  const find = (nodes: ArrangementTreeNode[]): ArrangementTreeNode | null => {
    for (const n of nodes) {
      const f = nodeFragment(source, n);
      if (f && marker(f) === folder) return n;
      const hit = find(n.children);
      if (hit) return hit;
    }
    return null;
  };
  let node: ArrangementTreeNode | null = null;
  for (const a of source.arrangements) {
    if (a.role === 'trash' || a.role === 'manuscript') continue;
    node = find(arrangementTree(a));
    if (node) break;
  }
  if (!node) return [];
  let number = 0;
  return node.children.flatMap((child) => {
    const fragment = nodeFragment(source, child);
    if (child.children.length || child.node.isFolder || !fragment) return [];
    // A note made the page (Scrivener's "Characters" page, a play's cast list) compiles as one.
    if (!(fragment.kind === 'prose' || fragment.kind === 'front-matter' || (fragment.kind === 'note' && marker(fragment) !== null))) return [];
    if (preset.select.respectIncludeInCompile && !fragment.includeInCompile) return [];
    const matter = marker(fragment);
    const generated = matter !== null && BookMatter.isGenerated(matter);
    const text = fill(fragment.text);
    // A play's (or radio play's) Cast of Characters left empty is made from the cast when it's set (`stageScript`); a
    // musical's Musical Numbers the same, and a play that isn't one any more prints none.
    const format = preset.render.screenplay?.format ?? 'screen';
    const play = isStageFormat(format) || format === 'radio';
    if (matter === 'musical-numbers' && !(play && source.musical)) return [];
    if (!generated && !((matter === 'cast-list' || matter === 'musical-numbers') && play) && trimAll(text) === '') return [];
    number += 1;
    return [section({
      role, title: fill(matter !== null ? BookMatter.printedTitle(matter) : fragment.title), number, fragmentID: fragment.id,
      blocks: generated ? [] : compileBlocks(text), matter,
      // A play's title page is the writer's only once changed.
      ...(play ? { source: fragment.text } : {}),
    })];
  });
}

const eqCase = (a: string | null, b: string) => a !== null && a.toLowerCase() === b.toLowerCase();

/** `PostPlace`: where a node stands among a journal's posts (`journalPosts`), for the `post` and `inPost` conditions. */
export type PostPlace = 'post' | 'inPost' | 'neither';

/** `matches`: a condition against an item with a document. */
export function matches(c: Condition, fragment: SourceFragment, node: ArrangementTreeNode, depth: number, source: CompileSource,
  place: PostPlace = 'neither'): boolean {
  if (c.collection !== undefined && !(collectionMembers(source, c.collection)?.has(fragment.id) ?? false)) return false;
  if (c.sectionType !== undefined && !eqCase(displayValue(source, fragment, StandardField.sectionType), c.sectionType)) return false;
  if (c.kind !== undefined && fragment.kind !== c.kind) return false;
  if (c.field !== undefined) {
    const value = displayValue(source, fragment, c.field);
    if (c.equals !== undefined) { if (!eqCase(value, c.equals)) return false; }
    else if (value === null) return false;
  }
  return structuralMatch(c, node, depth, place);
}

/** `structuralMatch`: the parts of a condition about the tree. */
export function structuralMatch(c: Condition, node: ArrangementTreeNode, depth: number, place: PostPlace = 'neither'): boolean {
  if (c.post !== undefined && c.post !== (place === 'post')) return false;
  if (c.inPost !== undefined && c.inPost !== (place === 'inPost')) return false;
  if (c.hasChildren !== undefined && c.hasChildren !== (node.children.length > 0 || node.node.isFolder)) return false;
  if (c.hasSubfolders !== undefined && c.hasSubfolders !== node.children.some((ch) => ch.children.length > 0 || ch.node.isFolder)) return false;
  if (c.depth !== undefined && c.depth !== depth) return false;
  if (c.sectionType !== undefined || c.kind !== undefined || c.field !== undefined || c.collection !== undefined) return node.node.fragmentID !== undefined;
  return true;
}

/** `joining`: a joined item's blocks on the end of `section`'s, its notes renumbered on from the section's (a numbered
 *  label counts on from the highest number there; another label that's taken gets "-2", "-3"…). */
export function joining(blocks: Block[], fragmentID: string, onto: CompiledSection): CompiledSection {
  const section: CompiledSection = { ...onto, joined: [...(onto.joined ?? []), fragmentID] };
  if (!blocks.length) return section;
  const labels = (bs: Block[]): string[] => bs.flatMap((b) => [
    ...(b.kind.type === 'footnote' ? [b.kind.id] : []),
    ...b.inlines.flatMap((i) => (i.type === 'footnoteReference' ? [i.id] : [])),
  ]);
  // Numbered: 1–9 ASCII digits (as the Swift reads labels).
  const whole = (s: string) => (/^[0-9]{1,9}$/.test(s) ? Number(s) : null);
  const taken = new Set(labels(section.blocks));
  const highest = Math.max(0, ...[...taken].map(whole).filter((n): n is number => n !== null));
  const renamed = new Map<string, string>();
  for (const label of labels(blocks)) {
    if (renamed.has(label)) continue;
    const n = whole(label);
    let next = n !== null ? String(n + highest) : label;
    if (n === null && taken.has(next)) {
      let k = 2;
      while (taken.has(`${label}-${k}`)) k++;
      next = `${label}-${k}`;
    }
    renamed.set(label, next);
    taken.add(next);
  }
  section.blocks = [...section.blocks, ...blocks.map((b) => ({
    ...b,
    kind: b.kind.type === 'footnote' ? { ...b.kind, id: renamed.get(b.kind.id) ?? b.kind.id } : b.kind,
    inlines: b.inlines.map((i) => (i.type === 'footnoteReference' ? { ...i, id: renamed.get(i.id) ?? i.id } : i)),
  }))];
  return section;
}

/** `StageScript.actsWritten(in:)`: the acts "A Play in Two Acts" counts — numbered top-level folders (parts) with
 *  something written in them: their own text, or a scene under them. */
export function actsWritten(sections: CompiledSection[]): number {
  let count = 0;
  let act: { numbered: boolean; written: boolean } | null = null;
  const close = () => { if (act && act.numbered && act.written) count += 1; };
  for (const section of sections) {
    if (section.matter !== null) continue;
    const written = section.source !== null && !/^\p{White_Space}*$/u.test(section.source);
    switch (section.role) {
      case 'part':
        close();
        act = { numbered: section.numbered ?? true, written };
        break;
      case 'chapter': case 'scene':
        if (act && written) act.written = true;
        break;
      default: break;
    }
  }
  close();
  return count;
}

/** `CompiledManuscript.sample`: the opening chapters (≤ ~`words`), without matter, for a preview before building. */
export function sample(m: CompiledManuscript, chapters = 2, limit = 7000): CompiledManuscript {
  if (m.preset.select.document !== undefined || m.preset.select.onePost === true) return m;
  const kept: CompiledSection[] = [];
  let chaptersSeen = 0, words = 0;
  for (const s of m.sections) {
    if (s.matter !== null || s.role === 'front-matter' || s.role === 'back-matter') continue;
    if (s.role === 'chapter' || s.role === 'part') {
      if (s.role === 'chapter') chaptersSeen += 1;
      if (chaptersSeen > chapters || (words >= limit && kept.length)) break;
    } else if (words >= limit && kept.length) {
      break;
    }
    kept.push(s);
    words += wordCountOfBlocks(s.blocks);
  }
  // A play's title page counts the acts of the whole play, not of the scenes the sample keeps.
  const out: CompiledManuscript = { ...m, sections: kept, preset: structuredClone(m.preset), playActs: actsWritten(m.sections) };
  // Books open on their first chapter in a preview, not on a title page.
  if (m.preset.render.print || m.preset.format === 'epub') out.preset.render.titlePage.include = false;
  return out;
}
